// import external libraries
import dayjs from 'dayjs';

// import utils
import { getWeekFieldName } from 'components/shared/RosterViewDataGrid/hooks/useRosterTimelineColumns/utils';

// import constants
import { BACKLOG_CATEGORIES } from '../../Backlog.consts';

// import types
import type {
  BackLogResponseList,
  ClientResponse,
  EmployeeResponse,
  EventResponse,
  PlanningEntityType,
  ResourceResponse,
  WorkPlaceResponse
} from 'generated/index';
import type {
  RosterDay,
  WeekValue
} from 'components/shared/RosterViewDataGrid';
import type {
  BacklogFilterState,
  BacklogRow,
  BacklogTaskCategory,
  BacklogWeekScope
} from '../../Backlog.types';

export const ROW_HEIGHT = 44;
export const TIMELINE_COLUMN_WIDTH = 100;

/** Prefix that distinguishes an entity header row from the rows underneath it. */
const CARE_RECEIVER_ROW_ID_PREFIX = 'careReceiver:';

/**
 * The event-based task categories, in tile order. Their category rows list appointments (counted by
 * `outstanding`) and open a week detail per cell, rather than folding open a set of prescription rules.
 */
export const EVENT_CATEGORIES: BacklogTaskCategory[] = [
  'outsideTreatment',
  'appointmentOverlap'
];

export const isEventCategory = (category: BacklogTaskCategory): boolean =>
  EVENT_CATEGORIES.includes(category);

/**
 * Reads a backlog entry's untyped `planningEntity` into a plain id/name/number, absorbing the
 * shape inconsistency across the four kinds of planning entity the backlog can report on: a
 * client's id and name sit under different keys than an employee's, and resources and workplaces
 * carry no number at all. This is the single place that inconsistency is handled — everything
 * downstream reads the same three fields regardless of type.
 */
export const extractPlanningEntity = (
  planningEntity: unknown,
  type: PlanningEntityType
): { id: string; name: string; number?: number } => {
  // A row whose planningEntity the backend could not resolve (a non-null planningEntityType with a null
  // entity) would otherwise throw on the first property read, and since this runs in the takenlijst's
  // render path that takes the whole page down rather than the one row. Degrade to an empty entity so
  // only that row is affected — for both callers at once (buildBacklogRows here, and backlogEntityId in
  // Backlog.queries).
  if (!planningEntity || typeof planningEntity !== 'object') {
    return { id: '', name: '' };
  }

  switch (type) {
    case 'EMPLOYEE': {
      const employee = planningEntity as EmployeeResponse;
      return {
        id: employee.entityId ?? '',
        // The informal name, as the appointment form shows it — nameCompound is the administrative
        // form ("J Jansen (Jan)"), so using it here made the same person read two different ways
        // between the takenlijst and the drawer it opens. Falls back to it when there is no informal
        // name to show.
        name: employee.nameInformal ?? employee.nameCompound ?? '',
        number: employee.number
      };
    }
    case 'RESOURCE': {
      const resource = planningEntity as ResourceResponse;
      return { id: resource.id, name: resource.name };
    }
    case 'WORKPLACE': {
      const workPlace = planningEntity as WorkPlaceResponse;
      return { id: workPlace.entityId ?? '', name: workPlace.name ?? '' };
    }
    case 'CLIENT':
    default: {
      const client = planningEntity as ClientResponse;
      return {
        id: client.id,
        name: client.displayName ?? '',
        number: client.number
      };
    }
  }
};

type WeekBucket = {
  field: string;
  start: number;
  end: number;
};

// buildBacklogRows and buildDisplayRows both derive their week columns from the same `weeks`, which
// the hook memoises and only rebuilds when the window changes. Cache on the array reference so the
// second call reuses the first result rather than recomputing it — and so the two can never derive
// different fields from the same weeks.
const weekBucketsCache = new WeakMap<RosterDay[][], WeekBucket[]>();

/**
 * Derives the grid's week columns from the roster days the grid itself renders, so summaries can
 * be matched to columns by date containment. Recomputing ISO week numbers here instead would
 * drift from the grid's own columns around year boundaries.
 */
export const getWeekBuckets = (weeks: RosterDay[][]): WeekBucket[] => {
  const cached = weekBucketsCache.get(weeks);
  if (cached) return cached;

  const buckets = weeks
    .filter((week) => week.length > 0)
    .map((week) => ({
      field: getWeekFieldName(week[0].weekNr, week[0].date.getFullYear()),
      start: dayjs(week[0].date).startOf('day').valueOf(),
      end: dayjs(week[week.length - 1].date)
        .endOf('day')
        .valueOf()
    }));

  weekBucketsCache.set(weeks, buckets);
  return buckets;
};

const findBucketIndex = (buckets: WeekBucket[], isoDate: string): number => {
  // dayjs parses 'YYYY-MM-DD' as local midnight; new Date() would parse it as UTC and shift a
  // day in timezones behind UTC.
  const timestamp = dayjs(isoDate).startOf('day').valueOf();
  return buckets.findIndex(
    (bucket) => timestamp >= bucket.start && timestamp <= bucket.end
  );
};

/**
 * Turns a planning entity's events for an event-based category (appointments outside treatment, or
 * overlapping appointments) into internal rows grouped by activity — the shape the weeks-view
 * aggregates into one non-folding category row (see buildDisplayRows). Kept per activity so the
 * toolbar's activity filter still narrows them. Each week cell is a plain count of that activity's
 * events; they carry no planned/prescribed pair, so they always count as a task (hasDeviation), and
 * `outstanding` is the number of events the row stands for — summed into the tile count.
 */
const buildEventCategoryRows = (
  events: EventResponse[],
  entity: {
    id: string;
    name: string;
    number?: number;
    type: PlanningEntityType;
  },
  category: BacklogTaskCategory,
  buckets: WeekBucket[],
  emptyWeekData: Record<string, null>
): BacklogRow[] => {
  const eventsByActivityId = new Map<
    string,
    { activityName: string; occurrences: { date: string; key: string }[] }
  >();
  for (const event of events) {
    const { entityId, name } = event.activity;
    // Keyed on the date too, since a recurring appointment's occurrences share one event id.
    const occurrence = { date: event.date, key: `${event.id}:${event.date}` };
    const existing = eventsByActivityId.get(entityId);
    if (existing) {
      existing.occurrences.push(occurrence);
    } else {
      eventsByActivityId.set(entityId, {
        activityName: name,
        occurrences: [occurrence]
      });
    }
  }

  return [...eventsByActivityId.entries()].map(
    ([activityId, { activityName, occurrences }]) => {
      const weekData: Record<string, number | null> = { ...emptyWeekData };

      let outstanding = 0;
      const eventKeys: string[] = [];
      for (const { date, key } of occurrences) {
        const bucketIndex = findBucketIndex(buckets, date);
        if (bucketIndex === -1) continue;
        const field = buckets[bucketIndex].field;
        weekData[field] = ((weekData[field] as number | null) ?? 0) + 1;
        outstanding += 1;
        eventKeys.push(key);
      }

      return {
        id: `${entity.id}:${category}:${activityId}`,
        entityId: entity.id,
        entityName: entity.name,
        entityNumber: entity.number,
        entityType: entity.type,
        activityName,
        activityId,
        description: '',
        totalPlanned: 0,
        totalPrescribed: 0,
        totalDifference: 0,
        // Always a task: an event-based row is something to put right, so it is never pruned as
        // "fully planned".
        hasDeviation: true,
        outstanding,
        eventKeys,
        category,
        rowType: 'rule' as const,
        ...weekData
      };
    }
  );
};

/**
 * Flattens the backlog response into one row per (care receiver, prescription rule) and writes
 * each period summary into the week column it belongs to.
 *
 * `PeriodSummary.plannedAppointments` becomes `WeekValue.current` and
 * `prescribedAppointments` becomes `WeekValue.total`, so `WeekValueCell` renders
 * `current - total` — the same value the backend reports as `difference`.
 */
export const buildBacklogRows = (
  backlog: BackLogResponseList | undefined,
  weeks: RosterDay[][]
): BacklogRow[] => {
  const buckets = getWeekBuckets(weeks);

  if (!backlog?.length || !buckets.length) return [];

  const windowStart = buckets[0].start;
  const windowEnd = buckets[buckets.length - 1].end;

  const emptyWeekData = Object.fromEntries(
    buckets.map((bucket) => [bucket.field, null])
  );

  const rows = backlog.flatMap((entry) => {
    // The planning entity is an untyped object in the contract; planningEntityType is optional in
    // the type system though the API always populates it — default to CLIENT, the type every
    // pre-existing response carries.
    const type = (entry.planningEntityType ?? 'CLIENT') as PlanningEntityType;
    const entity = extractPlanningEntity(entry.planningEntity, type);

    const deviationRows = entry.prescriptionRuleSummaries.map((ruleSummary) => {
      const weekData: Record<string, WeekValue | null> = { ...emptyWeekData };

      let totalPlanned = 0;
      let totalPrescribed = 0;
      let hasDeviation = false;

      for (const periodSummary of ruleSummary.periodSummaries) {
        // A summary is not always a single week: a TOTAL period asks for N appointments anywhere in
        // its window and is reported as one summary spanning that whole window — which can start
        // before the visible weeks and/or end after them while still overlapping them.
        const periodStart = dayjs(periodSummary.startPeriod)
          .startOf('day')
          .valueOf();
        const periodEnd = dayjs(periodSummary.endPeriod)
          .startOf('day')
          .valueOf();

        // Drop only a period that does not overlap the window at all. Clamping just the tail (as an
        // earlier version did) silently discarded a period that overlaps from before the window, and
        // the row then read as fully planned and dropped out of the task list entirely.
        //
        // A clamped period still contributes its whole-period counts: the summary reports one pair of
        // numbers for its entire window, with no per-week split to prorate from. So a TOTAL period
        // reaching beyond the visible weeks adds all of its work to the row's Totaal and shows it in
        // the first visible week — deliberately, since counting nothing would read as fully planned.
        if (periodEnd < windowStart || periodStart > windowEnd) continue;

        const rawFirst = findBucketIndex(buckets, periodSummary.startPeriod);
        const rawLast = findBucketIndex(buckets, periodSummary.endPeriod);
        const firstIndex = rawFirst === -1 ? 0 : rawFirst;
        const lastIndex = rawLast === -1 ? buckets.length - 1 : rawLast;
        const cellColSpan = Math.max(1, lastIndex - firstIndex + 1);

        weekData[buckets[firstIndex].field] = {
          current: periodSummary.plannedAppointments,
          total: periodSummary.prescribedAppointments,
          cellColSpan
        };

        // Null out the spanned weeks so they do not render a cell of their own.
        for (let index = firstIndex + 1; index <= lastIndex; index++) {
          weekData[buckets[index].field] = null;
        }

        totalPlanned += periodSummary.plannedAppointments;
        totalPrescribed += periodSummary.prescribedAppointments;
        // Both directions count: a surplus is as much a task as a shortage.
        if (periodSummary.difference !== 0) hasDeviation = true;
      }

      const { prescriptionRule } = ruleSummary;

      return {
        id: `${entity.id}:${prescriptionRule.id}`,
        entityId: entity.id,
        entityName: entity.name,
        entityNumber: entity.number,
        entityType: type,
        activityName: prescriptionRule.activity.name,
        activityId: prescriptionRule.activity.entityId,
        description: prescriptionRule.description,
        totalPlanned,
        totalPrescribed,
        totalDifference: totalPlanned - totalPrescribed,
        hasDeviation,
        category: 'deviation' as const,
        rowType: 'rule' as const,
        ...weekData
      };
    });

    // Appointments this entity has that no prescription rule covers (outside treatment) and
    // appointments whose time slot overlaps another (a double-booking). Both are event-based
    // categories the weeks-view aggregates into one non-folding row per category (see buildDisplayRows).
    const outsideTreatmentRows = buildEventCategoryRows(
      entry.outsideTreatmentEvents ?? [],
      { ...entity, type },
      'outsideTreatment',
      buckets,
      emptyWeekData
    );

    const appointmentOverlapRows = buildEventCategoryRows(
      entry.appointmentOverlapEvents ?? [],
      { ...entity, type },
      'appointmentOverlap',
      buckets,
      emptyWeekData
    );

    return [
      ...deviationRows,
      ...outsideTreatmentRows,
      ...appointmentOverlapRows
    ];
  });

  // Sorted by name so every entity — client, employee, resource or workplace — reads in one
  // alphabetical run, the same order the week-detail drill-down uses, and lines up with the tiles.
  return rows.sort(
    (a, b) =>
      a.entityName.localeCompare(b.entityName) ||
      // Tie-break on the id so two entities sharing a name cannot interleave, which would give
      // buildDisplayRows two headers for the same block.
      a.entityId.localeCompare(b.entityId) ||
      a.activityName.localeCompare(b.activityName)
  );
};

/** Identifies a category row, and with it what the collapse state applies to. */
export const getCategoryRowKey = (
  entityId: string,
  category: BacklogTaskCategory
) => `${entityId}:${category}`;

const emptyRowFields = {
  activityName: '',
  activityId: '',
  description: '',
  totalPlanned: 0,
  totalPrescribed: 0,
  totalDifference: 0,
  hasDeviation: false
};

/**
 * Totals the week cells of a set of rule rows into what a category row shows: how much there is to do
 * that week.
 *
 * Absolute values, not a net. A rule short one appointment and a rule over by one are two things to
 * put right, not nothing — netting them would report zero and, worse, render the checkmark that says
 * the week is already met.
 *
 * The result is a plain number rather than a planned/prescribed pair, because a magnitude is what it
 * is: there is no pair of counts whose difference means "four things to fix".
 *
 * A value that spans several weeks — a TOTAL period — lands entirely on the week it starts in,
 * because that is where the rule row carries it. The aggregate therefore shows a spanned obligation
 * at its start rather than smeared across the span.
 */
/**
 * The work a single week cell stands for, as a non-negative magnitude: an outside-treatment cell
 * carries a plain count and adds straight in; a deviation cell carries a planned/prescribed pair whose
 * gap is the work to do. Anything else (empty cell) is zero. Shared so the grid's category totals and
 * the week-detail's category value are derived the same way.
 */
export const getWeekValueMagnitude = (
  value: string | number | boolean | WeekValue | null | undefined
): number => {
  if (typeof value === 'number') return value;
  if (!value || typeof value !== 'object') return 0;
  return Math.abs(value.current - value.total);
};

const sumOutstandingByWeek = (
  rows: BacklogRow[],
  weekFields: string[]
): Record<string, number | null> => {
  const summed: Record<string, number | null> = {};

  weekFields.forEach((field) => {
    let outstanding = 0;
    let hasValue = false;

    rows.forEach((row) => {
      const value = row[field];
      // Only a number or a week-value pair contributes; an empty or non-value cell (or the eventKeys
      // array, which is not a week field) leaves hasValue as is, so a week no row reports on stays null
      // rather than a misleading zero.
      if (
        typeof value !== 'number' &&
        (!value || typeof value !== 'object' || Array.isArray(value))
      ) {
        return;
      }
      outstanding += getWeekValueMagnitude(value);
      hasValue = true;
    });

    summed[field] = hasValue ? outstanding : null;
  });

  return summed;
};

/**
 * Turns the flat list of rule rows into what the grid renders: an entity header row, then one row per
 * task category found for that entity carrying the totals of its rules, then — unless the category is
 * collapsed — the rules themselves.
 *
 * Categories keep the order of {@link BACKLOG_CATEGORIES} so they do not jump around between
 * refreshes, and so the rows line up with the tile strip.
 *
 * Expects the rows to be grouped by entity already — {@link buildBacklogRows} sorts them that way and
 * filtering preserves the order. Run this *after* filtering, otherwise a category whose every rule was
 * filtered out would leave an orphaned header behind.
 */
export const buildDisplayRows = (
  ruleRows: BacklogRow[],
  weeks: RosterDay[][],
  collapsedCategoryKeys: Set<string> = new Set()
): BacklogRow[] => {
  if (!ruleRows.length) return [];

  const weekFields = getWeekBuckets(weeks).map(({ field }) => field);
  const displayRows: BacklogRow[] = [];

  const entityOrder: string[] = [];
  const rowsByEntity = new Map<string, BacklogRow[]>();

  ruleRows.forEach((row) => {
    const existing = rowsByEntity.get(row.entityId);
    if (existing) {
      existing.push(row);
      return;
    }
    entityOrder.push(row.entityId);
    rowsByEntity.set(row.entityId, [row]);
  });

  entityOrder.forEach((entityId) => {
    const entityRows = rowsByEntity.get(entityId) ?? [];
    const [{ entityName, entityNumber, entityType }] = entityRows;

    displayRows.push({
      ...emptyRowFields,
      id: `${CARE_RECEIVER_ROW_ID_PREFIX}${entityId}`,
      // 'careReceiver' now names the entity header row in general — clients, employees, resources
      // and workplaces alike — kept as-is to avoid a wide rename across the grid.
      rowType: 'careReceiver',
      entityId,
      entityName,
      entityNumber,
      entityType,
      category: entityRows[0].category
    });

    BACKLOG_CATEGORIES.forEach(({ category }) => {
      const categoryRows = entityRows.filter(
        (row) => row.category === category
      );
      if (!categoryRows.length) return;

      // The event-based categories (outside treatment, overlapping appointments) are a single,
      // non-folding row: their week cells are opened per day in the week-detail view instead of
      // expanded here, so they carry no rule children and no chevron.
      const isEventCategoryRow = isEventCategory(category);

      const key = getCategoryRowKey(entityId, category);
      const isExpanded = !collapsedCategoryKeys.has(key);
      const totalPlanned = categoryRows.reduce(
        (sum, row) => sum + row.totalPlanned,
        0
      );
      const totalPrescribed = categoryRows.reduce(
        (sum, row) => sum + row.totalPrescribed,
        0
      );
      const weekOutstanding = sumOutstandingByWeek(categoryRows, weekFields);
      // Summed from the very cells shown beside it, so the Total can never contradict the weeks. Note
      // this is not |totalPlanned - totalPrescribed|: a rule short a week and over the next nets out
      // over the period, while the work to do is one in each of those weeks.
      const outstanding = Object.values(weekOutstanding).reduce<number>(
        (sum, value) => sum + (value ?? 0),
        0
      );

      displayRows.push({
        ...emptyRowFields,
        id: key,
        rowType: 'category',
        entityId,
        entityName,
        entityType,
        category,
        // Deviations count their rules; event-based categories count their appointments (their Total).
        taskCount: isEventCategoryRow ? outstanding : categoryRows.length,
        isExpanded: isEventCategoryRow ? false : isExpanded,
        // Kept as the honest signed net for anything that wants it; the Total cell shows `outstanding`.
        totalPlanned,
        totalPrescribed,
        totalDifference: totalPlanned - totalPrescribed,
        outstanding,
        hasDeviation: categoryRows.some((row) => row.hasDeviation),
        ...weekOutstanding
      });

      if (!isEventCategoryRow && isExpanded) displayRows.push(...categoryRows);
    });
  });

  return displayRows;
};

/**
 * Client-side filters.
 *
 * The entity/activity narrowing now happens on the server — the response is already scoped to the
 * requested entities and activities, so this no longer has a dropdown predicate to apply. What has
 * to stay client-side is: pruning fully-planned rows when `showCompleted` is off (the grid shows a
 * wider week window than the request prunes on, so the row check still has to run here — see
 * {@link buildBacklogRows}), and the category-tile filter, since the tiles are a client-only view
 * setting.
 */
export const filterBacklogRows = (
  rows: BacklogRow[],
  filters: BacklogFilterState
): BacklogRow[] =>
  rows.filter((row) => {
    if (!filters.showCompleted && !row.hasDeviation) return false;
    return filters.activeCategories.includes(row.category);
  });

/**
 * One row's contribution to the tile counts for a single week.
 *
 * Each category is read from where that week's truth actually sits: a deviation from the week's own
 * cell, an event category from its per-week count, and an overlap from the occurrences dated inside the
 * week — the row's `outstanding` and `eventKeys` both span the whole window, so neither can be used as
 * they are. The occurrence key carries the date exactly because of this.
 */
const countRowForWeek = (
  row: BacklogRow,
  weekScope: BacklogWeekScope,
  counts: Partial<Record<BacklogTaskCategory, number>>,
  overlapOccurrenceKeys: Set<string>
): void => {
  if (row.category === 'appointmentOverlap') {
    (row.eventKeys ?? []).forEach((key) => {
      if (weekScope.dates.has(key.slice(key.lastIndexOf(':') + 1))) {
        overlapOccurrenceKeys.add(key);
      }
    });
    return;
  }

  const cell = row[weekScope.field];

  if (isEventCategory(row.category)) {
    const eventsThisWeek = typeof cell === 'number' ? cell : 0;
    if (eventsThisWeek) {
      counts[row.category] = (counts[row.category] ?? 0) + eventsThisWeek;
    }
    return;
  }

  // A rule counts as one task for this week when the week deviates — the same test the cell itself is
  // drawn from, so the tile and the column can never disagree.
  const weekValue = cell as WeekValue | null;
  if (weekValue && weekValue.current !== weekValue.total) {
    counts[row.category] = (counts[row.category] ?? 0) + 1;
  }
};

/**
 * How many tasks each tile stands for. For deviations one task is one prescription rule that deviates
 * somewhere in the visible period — the unit the planner sees as a row. For outside treatment one task
 * is one stray appointment (a card in the week view), so its rows count by their appointment total
 * rather than one-per-row.
 *
 * Overlap is the exception: a clashing appointment surfaces under every entity it is double-booked with
 * (a clash on a shared employee and a client shows under both), so summing per-row `outstanding` would
 * count that one appointment once per entity. It counts distinct occurrences instead. Outside treatment
 * keeps summing — an appointment outside two care receivers' treatment is two separate tasks, one each.
 *
 * Counts are taken over the server-scoped rows, before the tile filter, so a tile always shows what
 * selecting it would bring in.
 *
 * With a {@link BacklogWeekScope} the same counts are taken for that one week instead of the whole
 * window — what the tiles must read once the planner has drilled into a week, or they would go on
 * promising work that is not on the screen underneath them.
 */
export const getTaskCategoryCounts = (
  rows: BacklogRow[],
  weekScope?: BacklogWeekScope | null
): Partial<Record<BacklogTaskCategory, number>> => {
  const counts: Partial<Record<BacklogTaskCategory, number>> = {};
  const overlapOccurrenceKeys = new Set<string>();

  rows.forEach((row) => {
    if (weekScope) {
      countRowForWeek(row, weekScope, counts, overlapOccurrenceKeys);
      return;
    }
    if (!row.hasDeviation) return;
    if (row.category === 'appointmentOverlap') {
      (row.eventKeys ?? []).forEach((key) => overlapOccurrenceKeys.add(key));
      return;
    }
    // Other event-based categories count their appointments (outstanding); deviations count one per rule.
    const increment = isEventCategory(row.category) ? row.outstanding ?? 0 : 1;
    counts[row.category] = (counts[row.category] ?? 0) + increment;
  });

  if (overlapOccurrenceKeys.size) {
    counts.appointmentOverlap = overlapOccurrenceKeys.size;
  }

  return counts;
};

/**
 * The seven roster days of the week a cell field points at — the day columns of the week-detail view.
 * Matches the field back to its bucket, so the detail's days line up with the grid's week columns.
 *
 * The bucket index is reused to index `weeks.filter(week => week.length > 0)` directly: getWeekBuckets
 * builds its buckets from that same filtered list in the same order, so the two stay aligned — keep it
 * so if either side's filtering ever changes.
 */
export const getWeekDays = (
  weeks: RosterDay[][],
  weekField: string
): RosterDay[] => {
  const bucketIndex = getWeekBuckets(weeks).findIndex(
    ({ field }) => field === weekField
  );
  if (bucketIndex === -1) return [];
  return weeks.filter((week) => week.length > 0)[bucketIndex] ?? [];
};

/**
 * The outside-treatment events that fall in the given week. The activity and entity narrowing that used
 * to happen here is now done on the server — the response only carries events the toolbar's filters
 * allow — so the week is the only thing left to select on.
 */
export const selectWeekEvents = (
  events: EventResponse[] | undefined,
  weekDays: RosterDay[]
): EventResponse[] =>
  (events ?? []).filter((event) =>
    weekDays.some((day) => dayjs(event.date).isSame(day.date, 'day'))
  );
